import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeStyles } from '@mui/styles'
import {
  Accordion, AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Theme,
  Typography
} from '@mui/material'

import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import DatePickerComponent from '@professocial/professocialFormComponents/FormComponents/DatePickerComponent'
import { IUserStatisticData } from '../../shared/types/types'
import { useDispatch, useSelector } from 'react-redux'
import {
  exportPublishedPostsCountForTemplateStatisticsInitiated,
  exportPublishedTemplatesByCreatorForRangeInitiated,
  getInsightsForPublishedPostsBeforeInitiated,
  getInsightsForPublishedPostsInitiated,
  getInsightsForPublishedTemplatesInRange,
  getInsightsForPublishedTemplatesInRangeInitiated,
  getPublishedPostsByTemplateCountInitiated,
  getPublishedTemplateCountInitiated
} from '../../store/statistics/statistics.editor.actions'
import {
  GetAggregatedInsightsForPublishedPostsInRangeRequest, GetInsightsForPublishedTemplatesInRangeRequest,
  GetPublishedPostsByTemplateCountRequest,
  GetPublishedTemplateCountRequest
} from '@3m5/sm_publish-frontend-api'
import {
  PostingTypes,
  PostingTypesObj,
  PublishedTemplatePostTypes
} from '@professocial/professocialTypes/types/statisticTypes'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PostingBarChart from '../../shared/components/ChartComponents/PostingStats/PostingBarChart'
import { ChartData } from 'chart.js'
import {
  getAggregatedPublishedPostsByTemplateCounts,
  getAggregatedPublishedPostsInsights,
  getAggregatedPublishedPostsInsightsBefore,
  getAggregatedPublishedTemplateCounts, getPublishedPostsExportedStatistics,
  getPublishedTemplatesExportedStatistics, getPublishedTemplatesStatistics
} from '../../store/statistics/statistics.editor.selector'
import { AggregatedPublishedCounts, AggregatedPublishedPostInsights } from '@3m5/sm_publish-frontend-api'
import { getDayDifference } from '../../util/getDayDifference'
import PostingPieChart from '../../shared/components/ChartComponents/PostingStats/PostingPieChart'
import { format } from 'date-fns'
import {
  ExportPublishedPostsCountForTemplateStatisticsRequest,
  ExportPublishedTemplatesByCreatorForRangeRequest
} from '@professocialApi/professocial-core-frontend-api/apis/DefaultApi'
import { saveCSVFromBlob } from '../../util/saveCSVFromBlob'
import PostingSingleBarChart from '../../shared/components/ChartComponents/PostingStats/PostingSingleBarChart'
import { PublishedTemplatePostInsights } from '@3m5/sm_publish-frontend-api'

interface PostingFilterChosenOptionProps {
  date: string
  params: PostingTypesObj
}

interface PostingFilterOptionProps {
  date: DropDownElement[]
  params: PostingTypes[]
}

type CustomCheckbox = {
  id: string
  type: PostingTypes
}

type ObjType = {
  [key: string]: any
}

type ChartType = 'templates' | 'postings'

export const useStyles = makeStyles((theme: Theme) => ({
  row: {
    margin: 0,
    width: '100%',
  },
  row2: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  accordion: {
    border: 0,
    '&:first-child': {
      borderRadius: 0,
    },
    '&:last-child': {
      borderRadius: 0,
    },
    backgroundColor: theme.palette.common.white,
  },
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(3),
  },
  box: {
    margin: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.common.white,
  },
  boxItem: {
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    padding: theme.spacing(1),
    minWidth: '255px',
    maxWith: '25%',
  },
  header: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    paddingLeft: 0,
    paddingRight: 0,
  },
  pageParamTitle: {
    fontWeight: 'bold',
  },
}))

const getDateDaysBefore = (days: number, date = new Date()): Date => {
  return new Date(date.setDate(date.getDate() - days))
}

const EditorPostingStatistics: React.FC<IUserStatisticData> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [dateFrom, setDateFrom] = useState<Date>(getDateDaysBefore(7))
  const [dateTo, setDateTo] = useState<Date>(new Date())
  const [dateFromBefore, setDateFromBefore] = useState<Date>(getDateDaysBefore(15))
  const [dateToBefore, setDateToBefore] = useState<Date>(getDateDaysBefore(8))
  const [mappedPostInsightsDataset, setMappedPostInsightsDataset] = useState<ChartData | undefined>()

  const [chosenFilterOption, setChosenFilterOption] = useState<PostingFilterChosenOptionProps>({
    date: '7',
    params: { likes: true, comments: true, shares: true, impressions: true, reach: true },
  })

  const dispatchGetInsightsForPublishedPosts = useCallback((params: GetAggregatedInsightsForPublishedPostsInRangeRequest) => dispatch(getInsightsForPublishedPostsInitiated(params)), [dispatch])
  const dispatchGetInsightsForPublishedPostsBefore = useCallback((params: GetAggregatedInsightsForPublishedPostsInRangeRequest) => dispatch(getInsightsForPublishedPostsBeforeInitiated(params)), [dispatch])
  const dispatchInsightsForPublishedTemplates = useCallback((params: GetInsightsForPublishedTemplatesInRangeRequest) => dispatch(getInsightsForPublishedTemplatesInRangeInitiated(params)), [dispatch])
  const dispatchGetPublishedTemplateCount = useCallback((params: GetPublishedTemplateCountRequest) => dispatch(getPublishedTemplateCountInitiated(params)), [dispatch])
  const dispatchGetPublishedPostsByTemplateCount = useCallback((params: GetPublishedPostsByTemplateCountRequest) => dispatch(getPublishedPostsByTemplateCountInitiated(params)), [dispatch])
  const dispatchExportPublishedTemplatesStatistic = useCallback((params: ExportPublishedTemplatesByCreatorForRangeRequest) => dispatch(exportPublishedTemplatesByCreatorForRangeInitiated(params)), [dispatch])
  const dispatchExportPublishedPostsStatistic = useCallback((params: ExportPublishedPostsCountForTemplateStatisticsRequest) => dispatch(exportPublishedPostsCountForTemplateStatisticsInitiated(params)), [dispatch])

  const postingStatisticsData = useSelector(getAggregatedPublishedPostsInsights)
  const postingStatisticsDataBefore = useSelector(getAggregatedPublishedPostsInsightsBefore)
  const publishedTemplatesStatisticsData = useSelector(getPublishedTemplatesStatistics)
  const publishedPostsData = useSelector(getAggregatedPublishedPostsByTemplateCounts)
  const publishedTemplatesData = useSelector(getAggregatedPublishedTemplateCounts)
  const publishedTemplatesStatistic = useSelector(getPublishedTemplatesExportedStatistics)
  const publishedPostsStatistic = useSelector(getPublishedPostsExportedStatistics)

  const filterOptions: PostingFilterOptionProps = {
    date: [
      { key: '7', value: t('statisticsCommon:last7Days') },
      { key: '30', value: t('statisticsCommon:last30Days') },
      { key: '0', value: t('statisticsCommon:individual') },
    ],
    params: [PostingTypes.likes, PostingTypes.comments, PostingTypes.shares, PostingTypes.impressions, PostingTypes.reach],
  }

  const handleDateChange = (date: Date, setToFullDay: boolean, setterFunction: (date: Date) => void) => {
    setToFullDay
      ? date.setHours(23, 59, 59, 0)
      : date.setHours(0, 0, 0, 0)

    setterFunction(date)
  }

  const checkboxFilter: CustomCheckbox[] = [
    { id: 'likes', type: PostingTypes.likes },
    { id: 'comments', type: PostingTypes.comments },
    { id: 'shares', type: PostingTypes.shares },
    { id: 'impressions', type: PostingTypes.impressions },
    { id: 'reach', type: PostingTypes.reach },
  ]

  const paramChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const changedType = event.target.id as PostingTypes
    setChosenFilterOption({
      ...chosenFilterOption,
      params: { ...chosenFilterOption.params, [changedType]: event.target.checked }
    })
  }

  const dateFormatted = (date: Date) => {
    return format(date, 'dd.MM.yyyy').toString()
  }

  const removeKeyValue = (object: ObjType, params: PostingTypesObj) => {
    return Object.keys(object)
      .filter(objKey => params[objKey as keyof PostingTypesObj])
      .reduce((newObj: ObjType, key) => {
        newObj[key] = object[key]
        return newObj
      }, {})
  }

  const mapPostingStatisticsToDataset = (data: AggregatedPublishedPostInsights, dataBefore: AggregatedPublishedPostInsights): ChartData => {
    let dataToFilter: ObjType = data
    let dataBeforeToFilter: ObjType = dataBefore

    dataToFilter = removeKeyValue(dataToFilter, chosenFilterOption.params)
    dataBeforeToFilter = removeKeyValue(dataBeforeToFilter, chosenFilterOption.params)

    return {
      datasets: [
        {
          label: `${dateFormatted(dateFromBefore)} - ${dateFormatted(dateToBefore)}`,
          data: Object.values(dataBeforeToFilter)
        },
        {
          label: `${dateFormatted(dateFrom)} - ${dateFormatted(dateTo)}`,
          data: Object.values(dataToFilter)
        }
      ],
      labels: Object.keys(chosenFilterOption.params)
        .filter(param => chosenFilterOption.params[param as keyof PostingTypesObj])
        .map(param => t(`statisticsCommon:${param}`)),
    }
  }

  const mapSinglePostingStatisticsToDataset = (data: PublishedTemplatePostInsights[]): ChartData => {
    return {
      labels: data.map((item) =>
        t(`statisticsCommon:${item.postType}`) + ` (${format(new Date(item.postedAt), 'dd.MM.yyyy')})`),
      datasets: Object.keys(PublishedTemplatePostTypes).map(record => {
        return {
          label: t(`statisticsCommon:${record}`), // top Labels
          data: data.map((part: any) => part[record]),
        }
      })
    }
  }

  const mapPublishedDataToDataset = (data: AggregatedPublishedCounts): ChartData => {
    return {
      datasets: [
        {
          label: `${dateFormatted(dateFrom)} - ${dateFormatted(dateTo)}`,
          data: Object.values(data),
        }
      ],
      labels: Object.keys(data).map(item => t(`statisticsCommon:${item}`))
    }
  }

  const getStatisticsData = () => {
    dispatchGetInsightsForPublishedPosts({ from: dateFrom, to: dateTo })
    dispatchInsightsForPublishedTemplates({ from: dateFrom, to: dateTo })
    dispatchGetPublishedTemplateCount({ from: dateFrom, to: dateTo })
    dispatchGetPublishedPostsByTemplateCount({ from: dateFrom, to: dateTo })

    let dateFromBefore = new Date(dateFrom)
    let dateToBefore = new Date(dateFrom)
    const difference = getDayDifference(dateTo, dateFrom)

    dateFromBefore = new Date(getDateDaysBefore(difference - 1, dateFromBefore))
    dateToBefore = new Date(getDateDaysBefore(1, dateToBefore))

    setDateFromBefore(dateFromBefore)
    setDateToBefore(dateToBefore)

    dispatchGetInsightsForPublishedPostsBefore({ from: dateFromBefore, to: dateToBefore })
  }

  const csvExport = (type: ChartType) => {
    type === 'postings' && dispatchExportPublishedPostsStatistic({ from: dateFrom, to: dateTo })
    type === 'templates' && dispatchExportPublishedTemplatesStatistic({ from: dateFrom, to: dateTo })
  }

  const csvButton = (type: ChartType) => {
    return (
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <ButtonComponent
          color="secondary"
          variant="contained"
          text={t('account:csvExport')}
          aria-label="Download"
          startIcon="input"
          disableElevation
          onClick={() => csvExport(type)}
        />
      </Box>
    )
  }

  useEffect(() => {
    getStatisticsData()
  }, [])

  useEffect(() => {
    getStatisticsData()
  }, [dateFrom, dateTo])

  useEffect(() => {
    const days = parseInt(chosenFilterOption.date)

    if (days) {
      setDateTo(new Date())
      setDateFrom(getDateDaysBefore(days))
    }
  }, [chosenFilterOption.date])

  useEffect(() => {
    if (postingStatisticsData && postingStatisticsDataBefore) {
      setMappedPostInsightsDataset(mapPostingStatisticsToDataset(postingStatisticsData, postingStatisticsDataBefore))
    }
  }, [postingStatisticsData, postingStatisticsDataBefore, chosenFilterOption.params])

  useEffect(() => {
    if (publishedPostsStatistic) {
      saveCSVFromBlob(publishedPostsStatistic, dateFrom, dateTo)
    }
  }, [publishedPostsStatistic])

  useEffect(() => {
    if (publishedTemplatesStatistic) {
      saveCSVFromBlob(publishedTemplatesStatistic, dateFrom, dateTo)
    }
  }, [publishedTemplatesStatistic])

  return (
    <Grid container>
      <Grid container direction="row">
        <Grid container item xs={12}>
          <h3>{t('statisticsCommon:statistics')}</h3>
        </Grid>
      </Grid>
      <Grid container direction="row" sm={12} item className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>

            <Box className={classes.boxItem}>
              <DropDownComponent
                options={filterOptions.date}
                label={t('statisticsCommon:dateRange')}
                value={chosenFilterOption.date}
                size="small"
                onChange={(option: DropDownElement) => setChosenFilterOption({ ...chosenFilterOption, date: option.key })}
                disableClearable
              />
            </Box>
            <Box className={classes.boxItem}>
              <DatePickerComponent
                label={t('statisticsCommon:dateRangeFrom')}
                value={dateFrom}
                size="small"
                inputVariant="outlined"
                onChangeDate={date => date && handleDateChange(date, false, setDateFrom)}
                disabled={parseInt(chosenFilterOption.date) !== 0}
                maxDate={new Date()}
              />
            </Box>
            <Box className={classes.boxItem}>
              <DatePickerComponent
                label={t('statisticsCommon:dateRangeTo')}
                value={dateTo}
                inputVariant="outlined"
                size="small"
                onChangeDate={date => date && handleDateChange(date, true, setDateTo)}
                disabled={parseInt(chosenFilterOption.date) !== 0}
                minDate={dateFrom}
                maxDate={new Date()}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      <Grid container item direction="row" sm={12} className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Box alignSelf="center" sx={{ p: 1 }}>
              <Typography className={classes.pageParamTitle}>{t('statisticsCommon:pageParameters')}&nbsp;</Typography>
            </Box>
            {checkboxFilter.map((checkbox, index) => (
              <Box key={checkbox.id} flexGrow={index + 1 === checkboxFilter.length ? 1 : 0}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={checkbox.id}
                      color="primary"
                      checked={!!chosenFilterOption.params[checkbox.type]}
                      onChange={(event) => paramChanged(event)}
                    />
                  }
                  label={t(`statisticsCommon:${checkbox.id}`) as string} labelPlacement="end"
                />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.row}>
        <Grid container item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion defaultExpanded classes={{ root: classes.accordion, rounded: classes.accordion }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>{t('statisticsCommon:mainStat')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {mappedPostInsightsDataset && <PostingBarChart
                  colors={props.colors}
                  data={mappedPostInsightsDataset}
                />}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
      <Grid container columnSpacing={{ xs: 0, md: 1 }} direction="row" className={classes.row}>
        <Grid item xs={12} md={6} sx={{ pl: '0px !important' }}>
          <Paper className={classes.paper}>
            <Accordion defaultExpanded={true} classes={{ root: classes.accordion, rounded: classes.accordion }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="h3"
                  sx={{ mt: 0, mb: 0 }}>{t('statisticsCommon:actuallyPublishedPostings')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {publishedPostsData && <PostingPieChart
                  data={mapPublishedDataToDataset(publishedPostsData)}
                  colors={props.colors}
                />}
                {csvButton('postings')}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Accordion defaultExpanded={true} classes={{ root: classes.accordion, rounded: classes.accordion }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="h3"
                  sx={{ mt: 0, mb: 0 }}>{t('statisticsCommon:editorialPublishedPostings')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {publishedTemplatesData && <PostingPieChart
                  data={mapPublishedDataToDataset(publishedTemplatesData)}
                  colors={props.colors}
                />}
                {csvButton('templates')}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.row}>
        <Grid container item xs={12}>
          <Paper className={classes.paper}>
            <Accordion defaultExpanded classes={{ root: classes.accordion, rounded: classes.accordion }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>{t('statisticsCommon:singleStat')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {publishedTemplatesStatisticsData && <PostingSingleBarChart
                  colors={props.colors}
                  data={mapSinglePostingStatisticsToDataset(publishedTemplatesStatisticsData)}
                />}
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  )
}
export default EditorPostingStatistics
